import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Upload, 
  X, 
  File, 
  Image, 
  Video, 
  FileText, 
  CheckCircle, 
  AlertCircle,
} from 'lucide-react';
import { cn } from '../utils/cn';

export interface MediaFile {
  id: string;
  name: string;
  type: string;
  size: number;
  data: string; // base64 data
  url?: string;
  alt?: string;
  caption?: string;
  uploadedAt: number;
}

interface UploadProgress {
  fileId: string;
  progress: number;
  status: 'uploading' | 'completed' | 'error';
  error?: string;
}

interface MediaUploaderProps {
  onUpload: (files: MediaFile[]) => void;
  maxFiles?: number;
  maxSize?: number; // in MB
  acceptedTypes?: string[];
  className?: string;
}

const MediaUploader: React.FC<MediaUploaderProps> = ({
  onUpload,
  maxFiles = 10,
  maxSize = 12,
  acceptedTypes = ['image/*', 'video/*', 'application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword', 'application/vnd.openxmlformats-officedocument.presentationml.presentation', 'application/vnd.ms-powerpoint'],
  className
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([]);
  const [previews, setPreviews] = useState<MediaFile[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (type: string) => {
    if (type.startsWith('image/')) return Image;
    if (type.startsWith('video/')) return Video;
    if (type === 'application/pdf') return FileText;
    if (type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || 
        type === 'application/msword') return FileText;
    if (type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
        type === 'application/vnd.ms-powerpoint') return FileText;
    return File;
  };

  const validateFile = (file: File): string | null => {
    // Check file size
    if (file.size > maxSize * 1024 * 1024) {
      return `File size must be less than ${maxSize}MB`;
    }

    // Check file type - including extensions for Office docs
    const fileExtension = file.name.toLowerCase().split('.').pop();
    const isValidExtension = fileExtension && ['doc', 'docx', 'ppt', 'pptx', 'pdf'].includes(fileExtension);
    
    const isValidType = acceptedTypes.some(type => {
      if (type.endsWith('/*')) {
        return file.type.startsWith(type.slice(0, -1));
      }
      return file.type === type;
    });

    if (!isValidType && !isValidExtension) {
      return `File type ${file.type || fileExtension} is not supported`;
    }

    return null;
  };

  const convertToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const compressImage = (file: File, maxWidth: number = 1920, maxHeight: number = 1080, quality: number = 0.8): Promise<File> => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        // Calculate new dimensions
        let { width, height } = img;
        
        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;

        // Draw and compress
        ctx?.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob((blob) => {
          if (blob) {
            const compressedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now()
            });
            resolve(compressedFile);
          } else {
            reject(new Error('Failed to compress image'));
          }
        }, file.type, quality);
      };

      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  };

  const processFile = async (file: File): Promise<MediaFile> => {
    let processedFile = file;
    
    // Compress images if they're larger than 1MB
    if (file.type.startsWith('image/') && file.size > 1024 * 1024) {
      try {
        processedFile = await compressImage(file);
      } catch (error) {
        console.warn('Failed to compress image, using original:', error);
      }
    }
    
    const data = await convertToBase64(processedFile);
    return {
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      name: processedFile.name,
      type: processedFile.type,
      size: processedFile.size,
      data,
      uploadedAt: Date.now()
    };
  };

  const handleFiles = useCallback(async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    
    // Validate files
    const validFiles: File[] = [];
    const errors: string[] = [];

    fileArray.forEach(file => {
      const error = validateFile(file);
      if (error) {
        errors.push(`${file.name}: ${error}`);
      } else {
        validFiles.push(file);
      }
    });

    if (errors.length > 0) {
      alert(errors.join('\n'));
    }

    if (validFiles.length === 0) return;

    // Check max files limit
    if (previews.length + validFiles.length > maxFiles) {
      alert(`Maximum ${maxFiles} files allowed`);
      return;
    }

    // Initialize upload progress
    const progressItems: UploadProgress[] = validFiles.map(file => ({
      fileId: file.name,
      progress: 0,
      status: 'uploading'
    }));
    setUploadProgress(progressItems);

    // Process files in batches to avoid overwhelming the browser
    const processedFiles: MediaFile[] = [];
    const batchSize = 3;
    
    for (let i = 0; i < validFiles.length; i += batchSize) {
      const batch = validFiles.slice(i, i + batchSize);
      
      const batchPromises = batch.map(async (file, batchIndex) => {
        const globalIndex = i + batchIndex;
        
        try {
          // Update progress to processing
          setUploadProgress(prev => 
            prev.map(item => 
              item.fileId === file.name 
                ? { ...item, progress: 10, status: 'processing' }
                : item
            )
          );
          
          const mediaFile = await processFile(file);
          processedFiles.push(mediaFile);
          
          // Update progress to completed
          setUploadProgress(prev => 
            prev.map(item => 
              item.fileId === file.name 
                ? { ...item, progress: 100, status: 'completed' }
                : item
            )
          );
        } catch (error) {
          console.error(`Error processing file ${file.name}:`, error);
          setUploadProgress(prev => 
            prev.map(item => 
              item.fileId === file.name 
                ? { ...item, progress: 0, status: 'error', error: 'Processing failed' }
                : item
            )
          );
        }
      });
      
      // Wait for current batch to complete before processing next batch
      await Promise.all(batchPromises);
    }

    // Add to previews
    setPreviews(prev => [...prev, ...processedFiles]);
    
    // Clear progress after a delay
    setTimeout(() => {
      setUploadProgress([]);
    }, 2000);
  }, [previews.length, maxFiles, maxSize, acceptedTypes]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFiles(e.target.files);
    }
  }, [handleFiles]);

  const removePreview = (fileId: string) => {
    setPreviews(prev => prev.filter(file => file.id !== fileId));
  };

  const handleUpload = () => {
    if (previews.length > 0) {
      onUpload(previews);
      setPreviews([]);
    }
  };

  return (
    <div className={cn("space-y-4", className)}>
      {/* Upload Zone */}
      <div
        className={cn(
          "border-2 border-dashed rounded-lg p-8 text-center transition-colors",
          isDragOver
            ? "border-[#667eea] bg-blue-50"
            : "border-gray-300 hover:border-gray-400"
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="space-y-4">
          <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
            <Upload className="w-6 h-6 text-gray-600" />
          </div>
          
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Upload Media Files
            </h3>
            <p className="text-gray-600 mb-4">
              Drag and drop files here, or click to browse
            </p>
            <p className="text-sm text-gray-500">
              Supports images, videos, and PDFs up to {maxSize}MB each
            </p>
          </div>
          
          <button
            onClick={() => fileInputRef.current?.click()}
            className="px-4 py-2 bg-[#667eea] text-white rounded-lg hover:bg-[#5a6fd8] transition-colors"
          >
            Choose Files
          </button>
        </div>
        
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={acceptedTypes.join(',') + ',.doc,.docx,.ppt,.pptx'}
          onChange={handleFileInput}
          className="hidden"
        />
      </div>

      {/* Upload Progress */}
      <AnimatePresence>
        {uploadProgress.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-2"
          >
            {uploadProgress.map((item) => (
              <div key={item.fileId} className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-900">{item.fileId}</span>
                  <div className="flex items-center space-x-2">
                    {item.status === 'completed' && (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    )}
                    {item.status === 'error' && (
                      <AlertCircle className="w-4 h-4 text-red-600" />
                    )}
                    {item.status === 'uploading' && (
                      <div className="w-4 h-4 border-2 border-[#667eea] border-t-transparent rounded-full animate-spin" />
                    )}
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <motion.div
                    className="bg-[#667eea] h-2 rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${item.progress}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
                {item.error && (
                  <p className="text-sm text-red-600 mt-1">{item.error}</p>
                )}
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* File Previews */}
      {previews.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-lg font-medium text-gray-900">
              Preview ({previews.length} files)
            </h4>
            <button
              onClick={handleUpload}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              Upload All
            </button>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {previews.map((file) => {
              const Icon = getFileIcon(file.type);
              return (
                <motion.div
                  key={file.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-white border border-gray-200 rounded-lg p-4 relative group"
                >
                  <button
                    onClick={() => removePreview(file.id)}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-3 h-3" />
                  </button>
                  
                  <div className="aspect-square bg-gray-100 rounded-lg flex items-center justify-center mb-3">
                    {file.type.startsWith('image/') ? (
                      <img
                        src={file.data}
                        alt={file.name}
                        className="w-full h-full object-cover rounded-lg"
                      />
                    ) : (
                      <Icon className="w-8 h-8 text-gray-400" />
                    )}
                  </div>
                  
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-gray-900 truncate" title={file.name}>
                      {file.name}
                    </p>
                    <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaUploader;
